import { format } from "date-fns";
import useSetSearchParams from "./useSetSearchParams";
import { useEffect, useMemo, useState } from "react";
import { deCodeOccupancies } from "@/utils/deCodeOccupancies";
import useHotelDetailStore from "@/core/stores/hotelDetail";
import getNextDate from "@/utils/getNextDay";
import { DateValueType } from '@/types/common'; 


export function scrollToElementById(elementId: string) {
  const element = document.getElementById(elementId);

  if (element) {
    // Get the element's top position relative to the document
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;
    // Scroll with a smooth behavior and apply the offset
    window.scrollTo({
      top: elementPosition - 150,
      behavior: "smooth",
    });
  } else {
    console.error(`Element not found with id: ${elementId}`);
  }
}

export function encodeToSlug(
  data: { adults: number; children: number[] }[],
): string {
  return data
    ?.map((room) => {
      const roomsData = `a${room.adults}c${room.children.length > 0 ? room.children.join("_") : "-1"}`;
      return encodeURIComponent(roomsData);
    })
    .join("__");
}

export function useSubmitHotelSearch() {
  const {
    params: { checkIn, checkOut, passenger = "a1c-1", std, end },
    setSearchParam,
  } = useSetSearchParams();
  const [isSubmiting, setIsSubmiting] = useState(false);

  const [errors, setErrors] = useState({
    city: "",
    date: "",
    passenger: "",
  });

  useEffect(() => {
    if (checkIn && checkOut) {
      setValues([new Date(checkIn), new Date(checkOut)]);
    } else if (std && end) {
      setValues([new Date(std), new Date(end)]);
    }

    if (passenger) {
      setNewRoomData(deCodeOccupancies(passenger as string));
    }
  }, [passenger, checkIn, checkOut]);

  const { isGetting } = useHotelDetailStore();

  useEffect(() => {
    setIsSubmiting(false);
  }, [isGetting]);

  const [values, setValues] = useState<(Date | null)[]>([
    new Date(getNextDate(1)),
    new Date(getNextDate(2)),
  ]);

  const [hasPassChanged, setHasPassChanged] = useState(false);

  const [newRoomData, setNewRoomData] = useState<
    { adults: number; children: number[] }[]
  >([{ adults: 1, children: [] }]);

  useEffect(() => {
    if (
      passenger !==
      encodeToSlug(
        newRoomData.map((room) => ({
          adults: room.adults,
          children: room.children.map((child) => Number(child)),
        })),
      )
    ) {
      setHasPassChanged(true);
    } else {
      setHasPassChanged(false);
    }
  }, [newRoomData]);

  const allPassengers = newRoomData.reduce((accumulator, room) => {
    return room.children.length + room.adults + accumulator;
  }, 0);

  const checkInTime = (shortMonth: boolean = true) => {
    return values[0]
      ? format(values[0], shortMonth ? "MMM d" : "MMMM d")
      : null;
  };

  const checkOutTime = (shortMonth: boolean = true) => {
    return values[1]
      ? format(values[1], shortMonth ? "MMM d" : "MMMM d")
      : null;
  };

  const [hasDateChanged, setHasDateChanged] = useState(false);

  const handleValueChange = (newValue: DateValueType) => {
    setHasDateChanged(true);
    setValues([
      newValue?.startDate ? new Date(newValue.startDate) : null,
      newValue?.endDate ? new Date(newValue.endDate) : null,
    ]);
  };

  const encodePassengerToSlug = useMemo(
    () =>
      encodeToSlug(
        newRoomData.map((room) => ({
          adults: room.adults,
          children: room.children.map((child) => Number(child)),
        })),
      ),
    [JSON.stringify(newRoomData)],
  );

  const onSubmitHotelSearch = async () => {
    const newPassengerParam = encodePassengerToSlug;
    let hasError = false;
    let NewErrors = {
      city: "",
      date: "",
      passenger: "",
    };
    if (!values || !values[0] || !values[1]) {
      NewErrors.date = "Please select a date";
      hasError = true;
    }

    if (allPassengers < 1) {
      NewErrors.passenger = "Please select at least one passenger";
      hasError = true;
    }

    if (
      newPassengerParam === passenger &&
      values[0] &&
      format(values[0], "yyyy-MM-dd") === checkIn &&
      values[1] &&
      format(values[1], "yyyy-MM-dd") === checkOut
    ) {
      NewErrors.passenger = "You haven't changed any item";
      hasError = true;
    }

    if (hasError) {
      setErrors(NewErrors);
      return;
    }

    setIsSubmiting(true);
    scrollToElementById("rooms");
    setHasDateChanged(false);
    setSearchParam([
      {
        passenger: encodeToSlug(
          newRoomData.map((room) => ({
            adults: room.adults,
            children: room.children.map((child) => Number(child)),
          })),
        ),
      },
      { checkIn: values[0] ? format(values[0], "yyyy-MM-dd") || "" : "" },
      { checkOut: values?.[1] ? format(values[1], "yyyy-MM-dd") : "" },
    ]);
  };


  const handleSubmitDate = async () => {
    let hasError = false;
    let NewErrors = { date: "", passenger: "" };

    if (!values || !values[0] || !values[1]) {
      NewErrors.date = "Please select a date";
      hasError = true;
      return;
    }
    scrollToElementById("rooms");
    setHasDateChanged(false);
    setSearchParam([
      { checkIn: format(values[0], "yyyy-MM-dd") || "" },
      { checkOut: values[1] ? format(values[1], "yyyy-MM-dd") : "" },
    ]);
  };

  const handleSubmitPassengers = async () => {
    let hasError = false;
    let NewErrors = { date: "", passenger: "" };

    if (allPassengers < 1) {
      NewErrors.passenger = "Please select at least one passenger";
      hasError = true;
    }
    scrollToElementById("rooms");
    setSearchParam([
      {
        passenger: encodeToSlug(
          newRoomData.map((room) => ({
            adults: room.adults,
            children: room.children.map((child) => Number(child)),
          })),
        ),
      },
    ]);
  };

  return {
    onSubmitHotelSearch,
    values,
    setValues,
    newRoomData,
    setNewRoomData,
    errors,
    isSubmiting,
    setIsSubmiting,
    handleSubmitDate,
    allPassengers,
    checkInTime,
    checkOutTime,
    handleValueChange,
    encodePassengerToSlug,
    handleSubmitPassengers,
    hasDateChanged,
    hasPassChanged,
  };
}
